import { useState, forwardRef, useImperativeHandle } from 'react'
import { BatchSelectDropdown, Batch } from './BatchSelectDropdown'
import { StaffMaterial } from '../../detail/StaffHistoryDetail';
import { Trash2 } from 'lucide-react';

export interface Allocation {
  batchId: number;
  brand: string;
  code: string;
  allocatedAmount: number;
  stock?: number; // Added to track max available stock for this specific batch
}

export interface StaffBorrowingDetailMaterialsRef {
  getAllocationSubmission: () => {
    allocations: { detailId: number; allocations: Allocation[] }[];
    noUsageDetailIds: number[];
  };
}

interface Props {
  materials: StaffMaterial[];
  isLoading?: boolean;
  onNoUsageSelectionChange?: (detailIds: number[]) => void;
}

export const StaffBorrowingDetailMaterials = forwardRef<StaffBorrowingDetailMaterialsRef, Props>(({ 
  materials, 
  isLoading,
  onNoUsageSelectionChange,
}, ref) => {
  const [edits, setEdits] = useState<Record<number, Allocation[]>>({})
  const [amountInputs, setAmountInputs] = useState<Record<number, Record<number, string>>>({})
  const [noUsage, setNoUsage] = useState<Record<number, boolean>>({})

  useImperativeHandle(ref, () => ({
    getAllocationSubmission: () => {
      const allocations = materials.map(mat => {
        const activeAllocations = edits[mat.id] !== undefined 
          ? edits[mat.id] 
          : mat.allocations.map(a => ({ 
              batchId: a.batch.id, 
              brand: a.batch.brand, 
              code: a.batch.code, 
              allocatedAmount: a.allocatedAmount,
              stock: (a.batch as Batch).size 
            }))
        
        const submittedAllocations = noUsage[mat.id]
          ? []
          : activeAllocations.map(allocation => {
              const rawAmount = amountInputs[mat.id]?.[allocation.batchId] ?? String(allocation.allocatedAmount)
              const amount = Number(rawAmount)
              if (rawAmount.trim() !== '' && (!Number.isFinite(amount) || amount < 0)) {
                throw new Error(`Enter a valid amount for ${mat.item.name}.`)
              }
              return { ...allocation, allocatedAmount: amount }
            })

        return {
          detailId: mat.id,
          allocations: submittedAllocations.filter(allocation => allocation.allocatedAmount > 0),
        };
      });

      return {
        allocations,
        noUsageDetailIds: materials.filter(mat => noUsage[mat.id]).map(mat => mat.id),
      };
    }
  }), [materials, edits, amountInputs, noUsage]);

  if (isLoading) {
    return (
      <div className="mt-6 flex flex-col gap-2 rounded-lg border bg-secondary/30 p-5 shadow-sm animate-pulse">
        <div className="h-4 w-40 bg-muted-foreground/20 rounded mb-4"></div>
        <div className="h-10 w-full bg-muted-foreground/20 rounded"></div>
      </div>
    )
  }
  
  if (!materials || materials.length === 0) return null

  const handleAddBatch = (detailId: number, batch: Batch & { currentSize?: number, size?: number }) => {
    const mat = materials.find(m => m.id === detailId)
    const current = edits[detailId] || mat?.allocations.map(a => ({
      batchId: a.batch.id, brand: a.batch.brand, code: a.batch.code, allocatedAmount: a.allocatedAmount, stock: (a.batch as Batch).size
    })) || []
    
    if (!current.find(c => c.batchId === batch.id)) {
      // 1. Calculate default amount: Request Amount (minus what is already allocated)
      const otherSum = current.reduce((sum, a) => sum + (a.allocatedAmount || 0), 0)
      const remainingNeeded = Math.max(0, (mat?.requestAmount || 0) - otherSum)
      
      // 2. Cap it at the actual stock the batch has available
      const batchStock = batch.currentSize ?? batch.size ?? Infinity
      const defaultAmount = Math.min(remainingNeeded, batchStock)

      setEdits({ 
        ...edits, 
        [detailId]: [...current, { 
          batchId: batch.id, 
          brand: batch.brand, 
          code: batch.code, 
          allocatedAmount: defaultAmount,
          stock: batchStock
        }] 
      })
      setAmountInputs({
        ...amountInputs,
        [detailId]: { ...amountInputs[detailId], [batch.id]: defaultAmount > 0 ? String(defaultAmount) : '' },
      })
    }
  }

  const handleRemoveBatch = (detailId: number, batchId: number) => {
    const current = edits[detailId] || materials.find(m => m.id === detailId)?.allocations.map(a => ({
      batchId: a.batch.id, brand: a.batch.brand, code: a.batch.code, allocatedAmount: a.allocatedAmount, stock: (a.batch as Batch).size
    })) || []

    setEdits({
      ...edits,
      [detailId]: current.filter(c => c.batchId !== batchId)
    })
    const nextAmounts = { ...amountInputs[detailId] }
    delete nextAmounts[batchId]
    setAmountInputs({ ...amountInputs, [detailId]: nextAmounts })
  }

  const handleUpdateAmount = (detailId: number, batchId: number, rawAmount: string, maxRequestAmount: number) => {
    const current = edits[detailId] || materials.find(m => m.id === detailId)?.allocations.map(a => ({
      batchId: a.batch.id, brand: a.batch.brand, code: a.batch.code, allocatedAmount: a.allocatedAmount, stock: (a.batch as Batch).size
    })) || []

    const targetAlloc = current.find(c => c.batchId === batchId)
    const batchStock = targetAlloc?.stock ?? Infinity

    const sumOfOtherBatches = current
      .filter(c => c.batchId !== batchId)
      .reduce((sum, a) => sum + (a.allocatedAmount || 0), 0)

    // Max allowed is capped by the request limit AND the specific batch's stock
    const maxAllowed = Math.min(Math.max(0, maxRequestAmount - sumOfOtherBatches), batchStock)

    const parsedAmount = Number(rawAmount)
    let displayAmount = rawAmount
    let amount = Number.isFinite(parsedAmount) && rawAmount.trim() !== '' && parsedAmount > 0 ? parsedAmount : 0
    if (amount > maxAllowed) {
      amount = maxAllowed
      displayAmount = String(maxAllowed)
    }

    if (rawAmount.trim() === '' || Number.isFinite(parsedAmount)) {
      setEdits({
        ...edits,
        [detailId]: current.map(c => c.batchId === batchId ? { ...c, allocatedAmount: amount } : c)
      })
    }
    setAmountInputs({
      ...amountInputs,
      [detailId]: { ...amountInputs[detailId], [batchId]: displayAmount },
    })
  }

  return (
    <div className="mt-6 flex flex-col gap-4 rounded-lg border bg-secondary/30 p-5 shadow-sm">
      <h4 className="text-sm font-semibold tracking-wide text-foreground uppercase">Material Allocations</h4>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {materials.map((mat) => {
          const activeAllocations = noUsage[mat.id] ? [] : edits[mat.id] !== undefined
            ? edits[mat.id] 
            : mat.allocations.map(a => ({ 
                batchId: a.batch.id, 
                brand: a.batch.brand, 
                code: a.batch.code, 
                allocatedAmount: a.allocatedAmount,
                stock: (a.batch as Batch).size 
              }))
          
          const totalAllocated = activeAllocations.reduce((sum, a) => sum + (a.allocatedAmount || 0), 0)
          const isFullyAllocated = totalAllocated >= mat.requestAmount

          return (
            <div key={mat.id} className="border border-border/50 rounded-md p-4 bg-background">
              <div className="flex justify-between items-center mb-3 border-b pb-2">
                <span className="font-semibold text-primary">{mat.item.name}</span>
                <span className="text-sm font-medium bg-secondary px-2 py-1 rounded">
                  Requested: {mat.requestAmount} {mat.item.sizeUnit}
                </span>
              </div>

              <label className="mb-4 flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
                <input
                  type="checkbox"
                  checked={Boolean(noUsage[mat.id])}
                  onChange={(event) => {
                    const checked = event.target.checked
                    const nextNoUsage = { ...noUsage, [mat.id]: checked }
                    setNoUsage(nextNoUsage)
                    onNoUsageSelectionChange?.(
                      materials.filter(material => nextNoUsage[material.id]).map(material => material.id)
                    )
                  }}
                />
                No material was used
              </label>

              {noUsage[mat.id] && (
                <p className="mb-4 rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
                  Saving this choice removes this material&apos;s batch allocations. No stock will be deducted.
                </p>
              )}

              <div className="space-y-3 divide-y divide-border/50 mb-4">
                {activeAllocations.map(alloc => {
                  const otherSum = activeAllocations.filter(a => a.batchId !== alloc.batchId).reduce((sum, a) => sum + (a.allocatedAmount || 0), 0)
                  const batchStock = alloc.stock ?? Infinity
                  
                  // Cap the visual Max constraint to stock limit or request limit
                  const maxAllowed = Math.min(Math.max(0, mat.requestAmount - otherSum), batchStock)

                  return (
                    <div key={alloc.batchId} className="flex gap-2 items-center text-sm pt-3 first:pt-0">
                      <span className="flex-1 text-muted-foreground">{alloc.brand} ({alloc.code})</span>
                      <input 
                        type="number" 
                        step="any" // Allows any decimal points (0.5, 0.01, etc.)
                        min={0}
                        max={maxAllowed}
                        className="w-24 border rounded px-2 py-1 text-sm bg-background"
                        value={amountInputs[mat.id]?.[alloc.batchId] ?? (alloc.allocatedAmount === 0 ? '' : String(alloc.allocatedAmount))}
                        onChange={(e) => handleUpdateAmount(mat.id, alloc.batchId, e.target.value, mat.requestAmount)}
                        onBlur={(e) => {
                          const val = Number(e.target.value)
                          if (e.target.value.trim() === '' || !Number.isFinite(val) || val <= 0) {
                            handleUpdateAmount(mat.id, alloc.batchId, '', mat.requestAmount)
                          } else {
                            handleUpdateAmount(mat.id, alloc.batchId, String(val), mat.requestAmount)
                          }
                        }}
                      />
                      <span className="text-xs uppercase text-muted-foreground w-8">{mat.item.sizeUnit}</span>
                      
                      <button
                        onClick={() => handleRemoveBatch(mat.id, alloc.batchId)}
                        className="flex justify-center w-10 text-red-500 hover:text-red-700 p-2 transition-colors"
                        title="Remove batch"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  )
                })}
                {activeAllocations.length === 0 && (
                  <div className="text-xs text-muted-foreground italic">
                    {noUsage[mat.id] ? 'No batch allocation will be saved.' : 'No batches allocated yet.'}
                  </div>
                )}
              </div>

              {!noUsage[mat.id] && <div className="flex flex-col gap-3 mt-4 pt-4 border-t border-border/50">
                <div className="w-full">
                  <BatchSelectDropdown 
                    itemId={mat.item.id} 
                    onSelect={(batch) => handleAddBatch(mat.id, batch)} 
                    disabled={isFullyAllocated}
                  />
                  {isFullyAllocated && (
                    <p className="text-xs font-medium text-emerald-600 mt-1">
                      ✓ Target amount reached
                    </p>
                  )}
                </div>
              </div>}
            </div>
          )
        })}
      </div>
    </div>
  )
})
